import React from 'react';
import { Search, X } from 'lucide-react';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import type { MaterialFolder, SupplierCatalog } from '../../types/material';

const CREATE_BTN_CLASS =
  'hidden md:inline-flex h-9 shrink-0 items-center rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]';

type MaterialSearchToolbarProps = {
  ordnerFolders: MaterialFolder[];
  supplierCatalogs: SupplierCatalog[];
  selectedFolderId: string | null;
  selectedSupplierId: string | null;
  onClearFilters: () => void;
  searchResultsCount: number;
  searchMode: 'semantic' | 'keyword' | 'exact' | null;
  searchPreference: 'semantic' | 'keyword';
  onSearchPreferenceChange: (mode: 'semantic' | 'keyword') => void;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearchHintClear: () => void;
  onTriggerSearch: () => void;
  onCancelSearch: () => void;
  searchSubmitting: boolean;
  onCreateClick: () => void;
};

export function MaterialSearchToolbar({
  ordnerFolders,
  supplierCatalogs,
  selectedFolderId,
  selectedSupplierId,
  onClearFilters,
  searchResultsCount,
  searchMode,
  searchPreference,
  onSearchPreferenceChange,
  searchInput,
  onSearchInputChange,
  onSearchHintClear,
  onTriggerSearch,
  onCancelSearch,
  searchSubmitting,
  onCreateClick,
}: MaterialSearchToolbarProps) {
  const activeFolder = ordnerFolders.find((f) => f.id === selectedFolderId);
  const activeSupplier = supplierCatalogs.find((s) => s.id === selectedSupplierId);

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-app-border px-4 py-3">
      <div className="hidden min-w-0 items-center gap-3 md:flex">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-app-text">
          <span>Materialien</span>
          {(activeFolder || activeSupplier) && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-app-border bg-app-surface-muted px-2 py-0.5 text-xs font-medium text-app-text-muted">
              {activeFolder
                ? `Ordner: ${activeFolder.name}`
                : `Großhändler: ${activeSupplier?.name ?? ''}`}
              <button
                type="button"
                onClick={onClearFilters}
                className="rounded-full p-0.5 text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
                aria-label="Filter entfernen"
                title="Filter entfernen"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </h2>
        <span className="shrink-0 text-xs text-app-text-subtle">
          {searchResultsCount.toLocaleString('de-DE')} Ergebnisse
          {searchMode === 'semantic' && ' · semantisch'}
          {searchMode === 'exact' && ' · Artikelnummer'}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
        <div
          className="inline-flex shrink-0 rounded-md border border-app-border bg-app-surface-muted p-0.5 text-xs font-medium"
          role="group"
          aria-label="Suchmodus"
        >
          <button
            type="button"
            onClick={() => onSearchPreferenceChange('keyword')}
            className={`rounded-md px-2.5 py-1.5 transition-colors ${
              searchPreference === 'keyword'
                ? 'bg-app-surface text-app-text shadow-sm'
                : 'text-app-text-subtle hover:text-app-text-muted'
            }`}
          >
            Stichwort
          </button>
          <button
            type="button"
            onClick={() => onSearchPreferenceChange('semantic')}
            className={`rounded-md px-2.5 py-1.5 transition-colors ${
              searchPreference === 'semantic'
                ? 'bg-app-surface text-app-text shadow-sm'
                : 'text-app-text-subtle hover:text-app-text-muted'
            }`}
          >
            Semantisch
          </button>
        </div>
        <div className="relative min-w-0 flex-1 md:max-w-[21rem] md:flex-none md:w-[21rem]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-app-text-subtle" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              onSearchInputChange(e.target.value);
              onSearchHintClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onTriggerSearch();
              }
            }}
            placeholder="Materialien durchsuchen…"
            className="h-9 w-full rounded-md border border-app-border py-0 pl-8 pr-8 text-sm outline-none focus:ring-2 focus:ring-pipedrive-blue/20"
          />
          {searchSubmitting ? (
            <button
              type="button"
              onClick={onCancelSearch}
              className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-app-text-subtle hover:bg-app-hover"
              aria-label="Suche abbrechen"
              title="Suche abbrechen"
            >
              <LoadingSpinner size="sm" />
            </button>
          ) : searchInput ? (
            <button
              type="button"
              onClick={() => onSearchInputChange('')}
              className="absolute right-1.5 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-app-text-subtle hover:bg-app-hover hover:text-app-text"
              aria-label="Suche leeren"
              title="Suche leeren"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <button type="button" onClick={onCreateClick} className={CREATE_BTN_CLASS}>
          + Material
        </button>
      </div>
    </div>
  );
}
